import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import api from "@/lib/api";
import { playTTS } from "@/lib/tts";
import { useAuth } from "@/context/AuthContext";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LevelSelect from "@/components/practice/LevelSelect";
import { Mic, Square, Loader2, Flag, Volume2 } from "lucide-react";
import { toast } from "sonner";

export default function SpeakingTab() {
  const [session, setSession] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [sending, setSending] = useState(false);
  const [level, setLevel] = useState("intermediate");
  const scrollRef = useRef(null);
  const { user } = useAuth();
  const { isSupported, isListening, start, stop } = useSpeechRecognition("en-US");

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [session?.messages]);

  const speak = (text) => {
    playTTS(text, user?.settings);
  };

  const startMutation = useMutation({
    mutationFn: () => api.post("/ai/dialogue/start", null, { params: { level } }),
    onSuccess: ({ data }) => {
      setSession(data);
      setAnalysis(null);
      speak(data.messages[0].content);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Не удалось начать разговор"),
  });

  const sendVoiceMessage = async (transcript) => {
    setSending(true);
    try {
      const { data } = await api.post(`/ai/dialogue/${session.id}/message`, { message: transcript });
      setSession((s) => ({
        ...s,
        messages: [...s.messages, { role: "user", content: transcript }, { role: "assistant", content: data.reply }],
      }));
      speak(data.reply);
    } catch (e) {
      toast.error("Не удалось отправить сообщение");
    } finally {
      setSending(false);
    }
  };

  const endMutation = useMutation({
    mutationFn: () => api.post(`/ai/dialogue/${session.id}/end`),
    onSuccess: ({ data }) => setAnalysis(data),
  });

  const handleMicClick = () => {
    if (isListening) {
      stop();
      return;
    }
    start((transcript) => sendVoiceMessage(transcript));
  };

  if (!isSupported) {
    return (
      <div className="text-center py-16 text-muted-foreground" data-testid="speech-not-supported">
        Ваш браузер не поддерживает распознавание речи. Попробуйте Chrome или Edge на десктопе.
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="speaking-start-state">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Mic className="w-7 h-7 text-primary" />
        </div>
        <p className="font-medium">Говорите с AI вслух, используя изученные слова</p>
        <LevelSelect value={level} onValueChange={setLevel} testIdPrefix="speaking" />
        <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending} data-testid="start-speaking-button">
          {startMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Начать разговор голосом"}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[60vh]" data-testid="speaking-active-state">
      <div className="px-4 py-2 border-b border-border flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Тема: {session.topic}</p>
          <div className="flex gap-1 flex-wrap mt-1">
            {session.target_words.map((w) => <Badge key={w} variant="secondary">{w}</Badge>)}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => endMutation.mutate()} data-testid="end-speaking-button">
          <Flag className="w-4 h-4 mr-1" /> Завершить
        </Button>
      </div>

      {analysis ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="speaking-analysis">
          <p className="font-heading font-semibold">Результаты разговора</p>
          <div>
            <p className="text-xs text-emerald-600 font-semibold mb-1">Использовано правильно</p>
            <div className="flex gap-1 flex-wrap">{analysis.used_correctly?.map((w) => <Badge key={w} className="bg-emerald-100 text-emerald-700 border-0">{w}</Badge>)}</div>
          </div>
          <div>
            <p className="text-xs text-orange-600 font-semibold mb-1">Использовано неправильно</p>
            <div className="flex gap-1 flex-wrap">{analysis.used_incorrectly?.map((w) => <Badge key={w} className="bg-orange-100 text-orange-700 border-0">{w}</Badge>)}</div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-semibold mb-1">Не использовано</p>
            <div className="flex gap-1 flex-wrap">{analysis.not_used?.map((w) => <Badge key={w} variant="secondary">{w}</Badge>)}</div>
          </div>
          <Button onClick={() => setSession(null)} data-testid="new-speaking-session-button">Новый разговор</Button>
        </div>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="speaking-messages">
            {session.messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm flex items-center gap-2 ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {m.role === "assistant" && (
                    <button onClick={() => speak(m.content)} className="shrink-0">
                      <Volume2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {m.content}
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-border flex flex-col items-center gap-2">
            <button
              onClick={handleMicClick}
              disabled={sending}
              data-testid="speaking-mic-button"
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-200 ${
                isListening ? "bg-red-500 animate-pulse" : "bg-primary"
              } text-white disabled:opacity-50`}
            >
              {sending ? <Loader2 className="w-6 h-6 animate-spin" /> : isListening ? <Square className="w-5 h-5" /> : <Mic className="w-6 h-6" />}
            </button>
            <p className="text-xs text-muted-foreground" data-testid="speaking-mic-hint">
              {isListening ? "Слушаю... нажмите чтобы остановить" : "Нажмите и говорите"}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
